import React, { useState, useEffect, useRef, useMemo } from "react";
import { Reorder, useDragControls } from "framer-motion";
import "../styles/Match.scss";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TooltipComponent from "./tooltipComponent";

const Match = ({
  data,
  mode,
  answers,
  setAnswers,
  questionId,
  isAdmin,
  retest,
  isAttended,
  hideQuestionSubmit = false,
}) => {
  const [initialData, setInitialData] = useState([]);
  const [items, setItems] = useState([]);
  const [isSubmit, setIsSubmit] = useState(
    isAdmin || mode === "review" || false,
  );
  const [submitStage, setSubmitStage] = useState(
    isAdmin || mode === "review" ? "showing" : "initial",
  );

  const [overflowStates, setOverflowStates] = useState({});

  const controls = useDragControls();
  const itemRefs = useRef({});

  const parseContent = (content) => {
    if (typeof content === "string") {
      try {
        return JSON.parse(content);
      } catch (error) {
        console.error("Error parsing content JSON:", error);
        return {};
      }
    }
    return content;
  };

  const { questions } = useMemo(() => {
    let q = [];
    if (Array.isArray(data?.pairs)) {
      q = data.pairs;
    } else if (data?.content) {
      const parsedContent = parseContent(data.content);
      if (Array.isArray(parsedContent?.pairs)) {
        q = parsedContent.pairs;
      } else {
        q = parsedContent?.questions || [];
      }
    } else if (data?.questions) {
      q = data.questions;
    } else if (Array.isArray(data)) {
      q = data;
    }
    const filteredQuestions = q.filter(
      (item) =>
        item && typeof item.key === "string" && Array.isArray(item.values),
    );
    return { questions: filteredQuestions };
  }, [data]);

  const explanation = useMemo(() => {
    if (!data) return "";

    if (data.explanation) {
      return data.explanation;
    }

    if (data.content) {
      const parsedContent = parseContent(data.content);
      if (parsedContent && parsedContent.explanation) {
        return parsedContent.explanation;
      }
    }

    return "";
  }, [data]);

  const [hasInteracted, setHasInteracted] = useState(false);

  const shuffle = (data, isInitial = false) => {
    if (!Array.isArray(data) || data.length < 2) return;

    setInitialData(JSON.parse(JSON.stringify(data)));

    const leftCol = data[0];
    const rightCol = data[1];

    let shuffledArray = [...rightCol.values];
    let isSame = true;
    let attempts = 0;

    while (isSame && attempts < 10) {
      for (let i = shuffledArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledArray[i], shuffledArray[j]] = [
          shuffledArray[j],
          shuffledArray[i],
        ];
      }

      isSame = shuffledArray.every(
        (val, index) => val === rightCol.values[index],
      );
      attempts++;
    }

    if (isSame && shuffledArray.length > 1) {
      const temp = shuffledArray[0];
      shuffledArray[0] = shuffledArray[1];
      shuffledArray[1] = temp;
    }

    const newItems = [leftCol, { key: rightCol.key, values: shuffledArray }];
    setItems(newItems);

    if (!isInitial && setAnswers && questionId) {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: { value: newItems, isSubmitted: false },
      }));
    }
  };

  const checkOverflow = () => {
    const newOverflowStates = {};

    Object.keys(itemRefs.current).forEach((key) => {
      const element = itemRefs.current[key];
      if (element) {
        const textSpan = element.querySelector(".single-line-text");
        if (textSpan) {
          const clone = textSpan.cloneNode(true);
          clone.style.display = "inline";
          clone.style.width = "auto";
          clone.style.visibility = "hidden";
          clone.style.position = "absolute";
          clone.style.whiteSpace = "nowrap";
          document.body.appendChild(clone);
          const width = clone.offsetWidth;
          document.body.removeChild(clone);

          if (width > element.clientWidth) {
            newOverflowStates[key] = true;
          }
        } else {
          const isOverflowing =
            element.scrollWidth > element.clientWidth ||
            element.scrollHeight > element.clientHeight;
          newOverflowStates[key] = isOverflowing;
        }
      }
    });

    setOverflowStates(newOverflowStates);
  };

  const resetGame = () => {
    if (questions && questions.length >= 2) {
      shuffle(questions, true);
      setIsSubmit(false);
      setSubmitStage("initial");
      setOverflowStates({});
      itemRefs.current = {};
      setHasInteracted(false);
      if (setAnswers && questionId) {
        setAnswers((prev) => {
          const newAnswers = { ...prev };
          delete newAnswers[questionId];
          return newAnswers;
        });
      }
    }
  };

  const handleSubmit = () => {
    if (submitStage === "initial") {
      setIsSubmit(true);
      setSubmitStage("submitted");
      if (setAnswers && questionId) {
        setAnswers((prev) => ({
          ...prev,
          [questionId]: { value: items, isSubmitted: true },
        }));
      }
    } else if (submitStage === "submitted") {
      setSubmitStage("showing");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      checkOverflow();
    }, 100);

    return () => clearTimeout(timer);
  }, [items]);

  useEffect(() => {
    if (hasInteracted && setAnswers && questionId && items.length > 0) {
      setAnswers((prev) => ({
        ...prev,
        [questionId]: { value: items, isSubmitted: false },
      }));
    }
  }, [hasInteracted, items, questionId, setAnswers]);

  useEffect(() => {
    if (
      isAttended &&
      !retest &&
      !hasInteracted &&
      (!answers || !answers[questionId])
    ) {
      setHasInteracted(true);
    }
  }, [isAttended, retest, questionId, answers, hasInteracted]);

  useEffect(() => {
    if (isAdmin || mode === "review") {
      setIsSubmit(true);
      setSubmitStage("showing");
    }
    if (questions && questions.length >= 2) {
      const currentAnswer = answers ? answers[questionId] : null;
      if (currentAnswer) {
        const value = currentAnswer.value || currentAnswer;
        const isSubmittedFlag = currentAnswer.isSubmitted || false;

        setInitialData(JSON.parse(JSON.stringify(questions)));
        setItems(value);
        setHasInteracted(true);

        if (mode === "quiz" && isSubmittedFlag) {
          setIsSubmit(true);
          setSubmitStage("submitted");
        }
      } else {
        shuffle(questions, true);
      }
    }
  }, [questions, questionId, answers, isAdmin, mode]);

  // Do not overwrite user's selected answers with initialData.
  // Showing stage now renders the correct comparison side-by-side.

  if (!questions || questions.length < 2) {
    return <div className="mcq-empty">Not enough data to match.</div>;
  }

  return (
    <div
      className={`matchswap-main-container ${isSubmit ? "disabled" : ""}`}
      onPointerDownCapture={() => {
        if (!isSubmit && !hasInteracted) {
          setHasInteracted(true);
        }
      }}
    >
      {submitStage === "showing" && (
        <div className="user-answers-title">Your Answer</div>
      )}
      <div className="matchswap-container">
        {items?.map((item, i) => (
          <div className="matchswap-inner-container" key={item?.key}>
            <div className="matchswap-header">
              <span>{item.key}</span>
            </div>

            {i > 0 ? (
              <Reorder.Group
                axis="y"
                values={item?.values || []}
                onReorder={(newOrder) => {
                  if (isSubmit) return;
                  if (!hasInteracted) setHasInteracted(true);
                  const updatedItems = [...items];
                  updatedItems[i] = { ...updatedItems[i], values: newOrder };
                  setItems(updatedItems);
                }}
                as="div"
                className={`matchswap-body-container ${
                  isSubmit ? "disabled" : ""
                }`}
              >
                {item?.values?.map((value, indx) => {
                  const isCorrect =
                    initialData[1]?.values[indx] === items[1]?.values[indx];

                  return (
                    <Reorder.Item
                      key={value}
                      value={value}
                      drag={!isSubmit ? "y" : false}
                      whileDrag={{
                        scale: 1.05,
                        boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                        zIndex: 10,
                      }}
                      whileTap={{ scale: 0.98 }}
                      className={`matchswap-body-item ${
                        mode === "review" || submitStage === "showing"
                          ? isCorrect
                            ? "correct-answer"
                            : "wrong-answer"
                          : ""
                      }`}
                      dragControls={controls}
                      dragListener={true}
                    >
                      <div className="drag-handle">
                        <DragIndicatorIcon />
                      </div>
                      <div className="match-item-content">
                        <TooltipComponent className="single-line-text">{value}</TooltipComponent>
                      </div>
                    </Reorder.Item>
                  );
                })}
              </Reorder.Group>
            ) : (
              <Reorder.Group
                axis="y"
                values={item?.values || []}
                as="div"
                className="matchswap-body-container"
              >
                {item?.values?.map((value, indx) => {
                  return (
                    <Reorder.Item
                      key={value}
                      value={value}
                      className="matchswap-body-item left-side"
                      drag={false}
                    >
                      <div className="match-item-content">
                        <TooltipComponent className="single-line-text">{value}</TooltipComponent>
                      </div>
                    </Reorder.Item>
                  );
                })}
              </Reorder.Group>
            )}
          </div>
        ))}
      </div>

      {submitStage === "showing" && initialData.length >= 2 && (
        <div className="correct-matches-container">
          <div className="correct-matches-title">Correct Pairs</div>
          <div className="matchswap-container">
            {initialData.map((item, i) => (
              <div className="matchswap-inner-container" key={`correct-${item.key}`}>
                <div className="matchswap-header">
                  <span>{item.key}</span>
                </div>
                <div className="matchswap-body-container disabled">
                  {item.values.map((value) => (
                    <div
                      key={`correct-val-${value}`}
                      className={`matchswap-body-item disabled ${
                        i === 0 ? "left-side" : "correct-answer"
                      }`}
                    >
                      {i > 0 && (
                        <div className="drag-handle" style={{ opacity: 0.5 }}>
                          <DragIndicatorIcon />
                        </div>
                      )}
                      <div className="match-item-content">
                        <TooltipComponent className="single-line-text">{value}</TooltipComponent>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === "quiz" && !isAdmin && !hideQuestionSubmit && (
        <div className="matchswap-btns-container">
          {submitStage !== "showing" && (
            <div
              className={`matchswap-button ${isSubmit && submitStage === "initial" ? "disabled" : ""}`}
              onClick={handleSubmit}
            >
              <span className="button-icon">
                {submitStage === "initial" ? <SendIcon /> : <VisibilityIcon />}
              </span>
              <span className="matchswap-button-text">
                {submitStage === "initial" ? "Submit" : "Show Answer"}
              </span>
            </div>
          )}

          {isSubmit && submitStage !== "showing" && (
            <button className="matchswap-reset-btn" onClick={resetGame}>
              Reset
            </button>
          )}
        </div>
      )}

      {mode === "review" && explanation && (
        <div className="match-swap-crt-answer">
          <span className="title-exp">Explanation:</span>
          <div className="match-swap-crt-answer-text">
            <p>{explanation}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Match;
